'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import { Formik } from 'formik'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'

// walrus
import Box from '@components/kit/box'
import { BoxLink } from '@components/kit/link'
import Pressable from '@components/kit/pressable'
import Text from '@components/kit/text'
import submitForm from '@lib/submit-form'

// lib
import BadgePill from '@components/ui/badge-pill'
import DataTable, { DataTableColumn } from '@components/ui/data-table'
import SectionHeader from '@components/ui/section-header'
import { useNeedsAttentionStore } from '@store/NeedsAttention'
import { INeedsAttentionItem } from '@store/NeedsAttention/types'
import {
  DashboardFilterOption,
  toFilterOptions
} from './dashboard-filter-options'
import ReportsFilterSelect from './reports-filter-select.web'

/* -----------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */

export type MemberNeedsAttentionFormValues = {
  professional_ids?: number[]
}

export type MemberRowProps = {
  days_since_last_visit: number
  employer_name: string
  last_activity: string
  last_visit_date: string
  pharmd: string
  user_id: number
}

/* -----------------------------------------------------------------------------
 * activity labels
 * -------------------------------------------------------------------------- */

export const activityLabelMap: Record<string, string> = {
  ACTION_PLAN_CHANGE: 'Action plan change',
  ADD_MEDS: 'Add meds',
  ALLERGY_CHANGE: 'Allergy change',
  APP_ACCESS: 'App access',
  CALL_DISCUSSION: 'Call discussion',
  CHAT_MESSAGE: 'Chat',
  CHAT_READ: 'Chat read',
  CHAT_SUGGESTION_TRIGGERED: 'Chat suggestion triggered',
  CHAT_SUGGESTION_VIEW: 'Chat suggestion view',
  CONDITION_CHANGE: 'Condition change',
  DEPRESCRIBE_CHANGE: 'Deprescribe change',
  DEPRESCRIBE_STOP: 'Deprescribe stop',
  FEEDBACK: 'Feedback',
  INCENTIVE_ENROLL: 'Incentive enroll',
  MAGIC_LINK_ACCESSED: 'Magic link accessed',
  MEAL_CHANGE: 'Meal change',
  MED_FREQUENCY: 'Med frequency',
  NOTES_ADD: 'Notes add',
  ONBOARDING_COMPLETE: 'Onboarding',
  OPPORTUNITY_POLL_ANSWER: 'Opportunity poll answer',
  POLL_ANSWER: 'Poll answer',
  POLL_V2_ANSWER: 'Poll v2 answer',
  PREP_WORK: 'Prep work',
  SKILLS_QUESTION_ANSWER: 'Skills question answer',
  STEPS_CHANGE: 'Steps change',
  STOPPED_MEDS: 'Stopped meds',
  SYMPTOM_TRACKING: 'Tracker',
  WEB_VERSION_ACCESS: 'Web version access',
  WEIGHT_CHANGE: 'Weight change'
}

export function formatActivity(last_activity: string) {
  return activityLabelMap[last_activity] || last_activity
}

/* -----------------------------------------------------------------------------
 * constants
 * -------------------------------------------------------------------------- */

// days without a visit past which the age reads as overdue
const STALE_VISIT_DAYS = 10

/* -----------------------------------------------------------------------------
 * columns
 * -------------------------------------------------------------------------- */

const columns: DataTableColumn<MemberRowProps>[] = [
  {
    key: 'user_id',
    label: 'Member',
    render: row => (
      <BoxLink path={`/chat/${row.user_id}`}>
        <Text font='pro.text.13.medium' textColor='proAccent'>
          {row.user_id}
        </Text>
      </BoxLink>
    ),
    width: 110
  },
  {
    flex: 1,
    key: 'last_activity',
    label: 'Last activity',
    minWidth: 180,
    render: row => (
      <BadgePill
        bgColor='proInkTint'
        dotColor='proInk'
        label={formatActivity(row.last_activity)}
        labelColor='proInk'
      />
    )
  },
  {
    key: 'days_since_last_visit',
    label: 'Days since visit',
    render: row => {
      const isStale = row.days_since_last_visit >= STALE_VISIT_DAYS

      return (
        <Text
          font={isStale ? 'pro.text.13.medium' : 'pro.text.13.regular'}
          textColor={isStale ? 'proDanger' : 'proTextMuted'}
        >
          {row.days_since_last_visit}
        </Text>
      )
    },
    width: 150
  },
  {
    key: 'last_visit_date',
    label: 'Last visit',
    render: row => (
      <Text font='pro.text.13.regular' textColor='proTextMuted'>
        {row.last_visit_date
          ? moment(row.last_visit_date).format('MM/DD/YYYY')
          : '—'}
      </Text>
    ),
    width: 130
  },
  {
    flex: 1,
    key: 'employer_name',
    label: 'Employer',
    minWidth: 180,
    render: row => (
      <Text font='pro.text.13.regular' textColor='proTextBody'>
        {row.employer_name}
      </Text>
    )
  },
  {
    flex: 1,
    key: 'pharmd',
    label: 'PharmD',
    minWidth: 140,
    render: row => (
      <Text font='pro.text.13.regular' textColor='proTextMuted'>
        {row.pharmd}
      </Text>
    )
  }
]

/* -----------------------------------------------------------------------------
 * LoadMoreFooter
 * -------------------------------------------------------------------------- */

type LoadMoreFooterProps = {
  isFetchingNextPage: boolean
  onLoadNextPage: () => void
}

function LoadMoreFooter({
  isFetchingNextPage,
  onLoadNextPage
}: LoadMoreFooterProps) {
  return (
    <Box
      alignItems='center'
      borderColor='proDividerSoft'
      borderStyle='solid'
      borderTopWidth={1}
      py='12'
    >
      <Pressable disabled={isFetchingNextPage} onPress={onLoadNextPage}>
        <Text font='pro.text.12.semibold' textColor='proAccent'>
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Text>
      </Pressable>
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * FormikMemberNeedsAttention
 * -------------------------------------------------------------------------- */

type FormikMemberNeedsAttentionProps = {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  onLoadNextPage: () => void
  professionals: DashboardFilterOption[]
  results: INeedsAttentionItem[]
  total_results: number
}

export function FormikMemberNeedsAttention({
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  onLoadNextPage,
  professionals,
  results,
  total_results
}: FormikMemberNeedsAttentionProps) {
  return (
    <Box mb='40'>
      <SectionHeader
        count={total_results}
        description='Members whose last recorded activity suggests they need a touchpoint'
        title='Member needs attention'
      />

      {/* options need a professional id on the rows, so the filter stays
          hidden while none carries one */}
      {professionals.length > 0 ? (
        <Box alignItems='flex-start' flexDirection='row' mb='12'>
          <Box width={220}>
            <ReportsFilterSelect
              filterOptions={professionals}
              idKey='id'
              label='PHARMD'
              name='professional_ids'
            />
          </Box>
        </Box>
      ) : null}

      <DataTable<MemberRowProps>
        columns={columns}
        data={results}
        emptyDescription='Every member received the care they deserve.'
        emptyTitle='Well done!'
        footer={
          hasNextPage ? (
            <LoadMoreFooter
              isFetchingNextPage={isFetchingNextPage}
              onLoadNextPage={onLoadNextPage}
            />
          ) : null
        }
        getRowKey={row => String(row.user_id)}
        isLoading={isLoading}
      />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * MainMemberNeedsAttention
 * -------------------------------------------------------------------------- */

export default function MainMemberNeedsAttention() {
  const { getMemberNeedsAttention } = useNeedsAttentionStore.getState()
  const [professional_ids, setProfessionalIds] = useState<number[]>([])
  const [results, setResults] = useState<INeedsAttentionItem[]>([])
  const [professionals, setProfessionals] = useState<DashboardFilterOption[]>(
    []
  )
  const [totalResults, setTotalResults] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const [hasNextPage, setHasNextPage] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState<boolean>(false)

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true)
    const response = await getMemberNeedsAttention({
      page: 0,
      professional_ids
    })
    setResults(response?.results ?? [])
    setTotalResults(response?.total_events ?? 0)
    setPage(response?.page ?? 0)
    setHasNextPage(Boolean(response?.has_next_page))
    // filter options come from the unfiltered rows, so narrowing the dashboard
    // does not narrow the dropdown along with it
    if (professional_ids.length === 0) {
      setProfessionals(
        toFilterOptions(
          response?.results ?? [],
          item => item.professional_id,
          item => item.pharmd
        )
      )
    }
    setIsLoading(false)
  }, [getMemberNeedsAttention, professional_ids])

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  const loadNextPage = async () => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }
    setIsFetchingNextPage(true)
    const response = await getMemberNeedsAttention({
      page: page + 1,
      professional_ids
    })
    setResults(current => [...current, ...(response?.results ?? [])])
    setPage(response?.page ?? page + 1)
    setHasNextPage(Boolean(response?.has_next_page))
    setIsFetchingNextPage(false)
  }

  const handleFormSubmit = submitForm(
    async (values: MemberNeedsAttentionFormValues) => {
      setProfessionalIds(values.professional_ids ?? [])
    }
  )

  return (
    <Formik<MemberNeedsAttentionFormValues>
      initialValues={{ professional_ids: [] }}
      onSubmit={handleFormSubmit}
    >
      <FormikMemberNeedsAttention
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        onLoadNextPage={loadNextPage}
        professionals={professionals}
        results={results}
        total_results={totalResults}
      />
    </Formik>
  )
}
